import { useEffect, useState } from "react";
import { ArrowLeftRight, Download, FileImage, FileText } from "lucide-react";
import { businessCardApi, downloadBlob } from "#/features/business-cards/api";
import { useCardTemplates } from "#/features/business-cards/hooks";
import type { CardFieldPage } from "#/features/business-cards/types";
import { employeeVCardUrl } from "../api";
import type { EmployeeDetails } from "../types";

/**
 * Replaces the old client-side React-rendered card (html2canvas/jsPDF over a
 * hard-coded layout) — the card is now whatever blank PDF an admin uploaded
 * under Organisation > Business Card, stamped with this employee's data on
 * the backend. This just previews and downloads what the backend renders.
 */
export function EmployeeCardPreview({ employee }: { employee: EmployeeDetails }) {
  const { data: templates, isLoading: templatesLoading } = useCardTemplates();
  const activeTemplate = templates?.find((t) => t.active) ?? null;

  const [page, setPage] = useState<CardFieldPage>("front");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [downloading, setDownloading] = useState<"pdf" | "png" | null>(null);

  useEffect(() => {
    if (!activeTemplate) return;
    let currentUrl: string | null = null;
    setPreviewLoading(true);
    setPreviewError(false);
    businessCardApi
      .employeeCardPng(employee.id, page)
      .then((blob) => {
        currentUrl = window.URL.createObjectURL(blob);
        setPreviewUrl(currentUrl);
      })
      .catch(() => setPreviewError(true))
      .finally(() => setPreviewLoading(false));
    return () => {
      if (currentUrl) window.URL.revokeObjectURL(currentUrl);
    };
  }, [employee.id, page, activeTemplate?.id]);

  async function handleDownload(kind: "pdf" | "png") {
    setDownloading(kind);
    try {
      const blob = kind === "pdf"
        ? await businessCardApi.employeeCardPdf(employee.id)
        : await businessCardApi.employeeCardPng(employee.id, page);
      downloadBlob(blob, `business-card-${employee.id}.${kind}`);
    } finally {
      setDownloading(null);
    }
  }

  if (templatesLoading) return null;
  if (!activeTemplate) return null;

  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Business Card</p>
        {activeTemplate.hasBackPage && (
          <button
            type="button"
            onClick={() => setPage((p) => (p === "front" ? "back" : "front"))}
            className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-bold text-slate-600 hover:bg-slate-50"
          >
            <ArrowLeftRight className="h-3.5 w-3.5" />
            {page === "front" ? "Show back" : "Show front"}
          </button>
        )}
      </div>

      <div className="flex min-h-[140px] items-center justify-center rounded-xl border border-slate-200 bg-slate-50 p-2">
        {previewLoading ? (
          <span className="text-xs text-slate-400">Rendering…</span>
        ) : previewError ? (
          <span className="text-xs text-rose-500">Could not render the card preview.</span>
        ) : previewUrl ? (
          <img src={previewUrl} alt={`${employee.fullName}'s business card`} className="max-h-[200px] rounded shadow" />
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <a
          href={employeeVCardUrl(employee.id)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
        >
          <Download className="h-3.5 w-3.5" />
          Download contact
        </a>
        <button
          type="button"
          onClick={() => handleDownload("png")}
          disabled={downloading !== null}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <FileImage className="h-3.5 w-3.5" />
          Download image
        </button>
        <button
          type="button"
          onClick={() => handleDownload("pdf")}
          disabled={downloading !== null}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <FileText className="h-3.5 w-3.5" />
          Download PDF
        </button>
      </div>
    </div>
  );
}
